"use client";

import { useEffect, useMemo, useRef } from "react";
import { motion, useAnimation } from "framer-motion";

interface FrequencyArray extends Uint8Array {
  buffer: ArrayBuffer;
  subarray(begin?: number, end?: number): FrequencyArray;
  slice(begin?: number, end?: number): FrequencyArray;
}

export default function VoiceBubble() {
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const freqDataRef = useRef<FrequencyArray | null>(null);
  const rafRef = useRef<number | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mountedRef = useRef(false);

  const control1 = useAnimation();
  const control2 = useAnimation();
  const control3 = useAnimation();
  const control4 = useAnimation();
  const control5 = useAnimation();
  const control6 = useAnimation();
  const control7 = useAnimation();

  const blobControls = useMemo(
    () => [
      control1,
      control2,
      control3,
      control4,
      control5,
      control6,
      control7,
    ],
    [control1, control2, control3, control4, control5, control6, control7]
  );

  useEffect(() => {
    mountedRef.current = true;

    const animate = () => {
      if (!mountedRef.current) return;

      rafRef.current = requestAnimationFrame(animate);

      if (!analyserRef.current || !freqDataRef.current) return;

      analyserRef.current.getByteFrequencyData(freqDataRef.current);
      const avg =
        freqDataRef.current.reduce((sum, value) => sum + value, 0) /
        freqDataRef.current.length;
      const normalized = Math.min(avg / 50, 1);

      blobControls.forEach((control, index) => {
        const spread = 0.4 + index * 0.12;
        const random = 0.3 + Math.random() * 0.5;
        const level = normalized * (spread + random);

        control.start({
          scaleX: 0.9 + level * 0.5,
          scaleY: 0.85 + level * 1.6,
          borderRadius: `${45 + level * 25}% ${50 + level * 15}% ${
            48 + level * 20
          }% ${47 + level * 20}% / ${50 + level * 15}% ${45 + level * 25}% ${
            47 + level * 20
          }% ${48 + level * 20}%`,
          opacity: 0.2 + level * 0.6,
          transition: { duration: 0.25, ease: "easeOut" },
        });
      });
    };

    const init = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaStreamRef.current = stream;
        const audioContext = new (window.AudioContext ||
          (window as typeof window & { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
        freqDataRef.current = new Uint8Array(
          analyser.frequencyBinCount
        ) as FrequencyArray;

        animate();
      } catch (error) {
        console.error("VoiceBubble audio init failed:", error);
      }
    };

    init();

    return () => {
      mountedRef.current = false;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close().catch(() => undefined);
      }
      audioContextRef.current = null;
      analyserRef.current = null;
      freqDataRef.current = null;
    };
  }, [blobControls]);

  return (
    <div className="fixed bottom-0 h-[100px] sm:h-[120px] md:h-[140px] lg:h-[160px] flex items-center justify-center bg-transparent z-50 pointer-events-none">
      <div className="flex items-center gap-0">
        {blobControls.map((control, index) => (
          <motion.div
            key={index}
            animate={control}
            className={`w-[80px] h-[40px] sm:w-[100px] sm:h-[45px] md:w-[120px] md:h-[50px] lg:w-[140px] lg:h-[55px] bg-gradient-to-br ${
              index === 0
                ? "from-pink-200 via-pink-300 to-pink-400"
                : index === 1
                ? "from-pink-300 via-pink-400 to-pink-500"
                : index === 2
                ? "from-pink-400 via-pink-500 to-fuchsia-400"
                : index === 3
                ? "from-pink-500 via-fuchsia-400 to-fuchsia-500"
                : index === 4
                ? "from-fuchsia-400 via-fuchsia-500 to-purple-400"
                : index === 5
                ? "from-fuchsia-500 via-purple-400 to-purple-500"
                : "from-purple-400 via-purple-500 to-purple-600"
            } blur-2xl opacity-30 -mx-4`}
            transition={{ duration: 0.2, ease: "easeOut" }}
            style={{
              borderRadius: "50% 48% 47% 49% / 48% 50% 49% 47%",
            }}
          />
        ))}
      </div>
    </div>
  );
}
